import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import useDebouncedValue from "@/lib/useDebouncedValue";
import { arePlanInputsValid, errorMessage } from "@/lib/errorMessage";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ReferenceLine,
  Legend,
} from "recharts";
import { fmtCurrency, fmtCurrencyFull } from "@/lib/format";
import { Loader2, GitBranch } from "lucide-react";

// Color scale from red (bad early returns) -> green (good early returns)
const colorFor = (rankPct) => {
  // 0% rank -> red, 100% -> green
  const t = rankPct / 100;
  const r = Math.round(194 + (52 - 194) * t);   // C26747 -> 348C59
  const g = Math.round(103 + (140 - 103) * t);
  const b = Math.round(71 + (89 - 71) * t);
  return `rgb(${r},${g},${b})`;
};

const CHART_MARGIN = { top: 12, right: 16, left: 0, bottom: 0 };
const TOOLTIP_STYLE = { background: "#fff", border: "1px solid #E5E2DC", borderRadius: 12, fontSize: 11 };
const X_TICK = { fill: "#5C6B62", fontSize: 11 };
const X_AXIS_LINE = { stroke: "#E5E2DC" };
const LEGEND_WRAPPER = { fontSize: 10, color: "#5C6B62" };
const LEGEND_PAYLOAD = [
  { value: "Bad early returns", type: "line", color: "#C26747" },
  { value: "Average", type: "line", color: "#728A7C" },
  { value: "Good early returns", type: "line", color: "#348C59" },
];

export default function SamplePathsViz({ inputs, retirementAge }) {
  const [loading, setLoading] = useState(false);
  const [paths, setPaths] = useState(null);
  const [earlyWindow, setEarlyWindow] = useState(0);

  const fetch = async () => {
    if (!arePlanInputsValid(inputs)) return;
    setLoading(true);
    try {
      const { data } = await api.post("/projections/sample_paths", { ...inputs, n_paths: 8 });
      setPaths(data.paths);
      setEarlyWindow(data.early_window_years);
    } catch (e) {
      console.error("sample_paths fetch failed:", errorMessage(e, "sample paths failed"));
    } finally {
      setLoading(false);
    }
  };

  // Debounce age-sensitive inputs so we don't fire a request on every keystroke.
  const dAge = useDebouncedValue(inputs.current_age, 600);
  const dRet = useDebouncedValue(inputs.retirement_age, 600);
  const dLife = useDebouncedValue(inputs.life_expectancy, 600);
  const dSavings = useDebouncedValue(inputs.current_savings, 600);
  const dContrib = useDebouncedValue(inputs.monthly_contribution, 600);
  const dWithdraw = useDebouncedValue(inputs.annual_withdrawal, 600);
  const dInflation = useDebouncedValue(inputs.inflation_rate, 600);

  // True between a keystroke and the debounced value catching up — gives
  // the user a quiet "we registered your edit, results will refresh shortly" signal.
  const pending =
    dAge !== inputs.current_age ||
    dRet !== inputs.retirement_age ||
    dLife !== inputs.life_expectancy ||
    dSavings !== inputs.current_savings ||
    dContrib !== inputs.monthly_contribution ||
    dWithdraw !== inputs.annual_withdrawal ||
    dInflation !== inputs.inflation_rate;

  useEffect(() => {
    fetch();
    // eslint-disable-next-line
  }, [
    dAge,
    dRet,
    dLife,
    dSavings,
    dContrib,
    dWithdraw,
    inputs.allocation.stocks,
    inputs.allocation.bonds,
    inputs.allocation.cash,
    dInflation,
  ]);

  // Merge all paths by age for recharts
  const merged = useMemo(() => {
    if (!paths || !paths.length) return [];
    const byAge = {};
    paths.forEach((p, i) => {
      p.trajectory.forEach((pt) => {
        if (!byAge[pt.age]) byAge[pt.age] = { age: pt.age };
        byAge[pt.age][`path_${i}`] = pt.balance;
      });
    });
    return Object.values(byAge).sort((a, b) => a.age - b.age);
  }, [paths]);

  return (
    <div data-testid="sample-paths-viz" className="bg-white border border-[#E5E2DC] rounded-2xl p-6 sm:p-8">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold inline-flex items-center gap-2">
            <GitBranch size={12} /> sequence-of-returns risk
          </div>
          <div className="font-display text-lg text-[#1C2B23] mt-1">
            Same plan, different luck.
          </div>
        </div>
        {(pending || loading) && (
          <span
            data-testid="sample-paths-pending"
            className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase text-[#5C6B62] bg-[#F0EFEA] border border-[#E5E2DC] rounded-full px-2.5 py-1"
          >
            <Loader2 size={11} className="animate-spin" />
            {pending ? "applying changes…" : "running…"}
          </span>
        )}
      </div>
      <p className="text-sm text-[#5C6B62] mb-5 max-w-2xl leading-relaxed">
        Eight Monte Carlo paths shown, ranked by their first {earlyWindow || 5}-year return at retirement.
        Red lines had bad early luck; green lines had good early luck. <span className="text-[#1C2B23] font-medium">Order of returns matters as much as average return</span>.
      </p>
      <div className="h-72" style={{ minHeight: 280 }}>
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
          <LineChart data={merged} margin={CHART_MARGIN}>
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis dataKey="age" tick={X_TICK} axisLine={X_AXIS_LINE} tickLine={false} />
            <YAxis tickFormatter={(v) => fmtCurrency(v)} tick={X_TICK} axisLine={false} tickLine={false} width={70} />
            <Tooltip
              contentStyle={TOOLTIP_STYLE}
              formatter={(v, name) => [fmtCurrencyFull(v), name.replace("path_", "Path ")]}
              labelFormatter={(l) => `age ${l}`}
            />
            {retirementAge && (
              <ReferenceLine x={retirementAge} stroke="#C26747" strokeDasharray="4 4" label={{ value: "retire", fill: "#C26747", fontSize: 11, position: "top" }} />
            )}
            {(paths || []).map((p) => (
              <Line
                key={`${p.rank_pct}-${p.early_return_pct}`}
                type="monotone"
                dataKey={`path_${(paths || []).indexOf(p)}`}
                stroke={colorFor(p.rank_pct)}
                strokeWidth={1.5}
                dot={false}
                isAnimationActive={false}
                opacity={0.85}
              />
            ))}
            <Legend
              verticalAlign="bottom"
              height={20}
              wrapperStyle={LEGEND_WRAPPER}
              payload={LEGEND_PAYLOAD}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
